import {css, StyleDeclaration, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {PURPLE_DARK, WHITE} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {AvatarShape, AvatarSize} from './AvatarConstants';

export enum AvatarFrameType {
  EDITOR,
}

const ICON_BY_FRAME_TYPE: Record<AvatarFrameType, BenchlingIconName | null> = {
  [AvatarFrameType.EDITOR]: 'pen',
};

const COLOR_BY_FRAME_TYPE: Record<AvatarFrameType, string> = {
  [AvatarFrameType.EDITOR]: PURPLE_DARK,
};

export interface AvatarFrameProps {
  type: AvatarFrameType;
  size: AvatarSize;
  shape: AvatarShape;
  className?: string;
}

export default function AvatarFrame(props: AvatarFrameProps): JSX.Element {
  const frameColor = COLOR_BY_FRAME_TYPE[props.type];
  const frameIcon = ICON_BY_FRAME_TYPE[props.type];

  const frameShapeStyle: Record<AvatarShape, StyleDeclaration> = {
    [AvatarShape.SQUARE]: styles.squareFrame,
    [AvatarShape.CIRCLE]: styles.circleFrame,
  };
  const frameSizeStyle: Record<AvatarSize, StyleDeclaration> = {
    [AvatarSize.XXSMALL]: styles.xxsFrame,
    [AvatarSize.XSMALL]: styles.xsFrame,
    [AvatarSize.SMALL]: styles.smallFrame,
    [AvatarSize.NEWSMALL]: styles.smallFrame,
    [AvatarSize.LARGE]: styles.largeFrame,
    [AvatarSize.XLARGE]: styles.xlFrame,
  };
  const iconSizeStyle: Record<AvatarSize, StyleDeclaration> = {
    [AvatarSize.XXSMALL]: styles.iconSmall,
    [AvatarSize.XSMALL]: styles.iconSmall,
    [AvatarSize.SMALL]: styles.iconMedium,
    [AvatarSize.NEWSMALL]: styles.iconMedium,
    [AvatarSize.LARGE]: styles.iconMedium,
    [AvatarSize.XLARGE]: styles.iconMedium,
  };

  return (
    <div
      className={classNames(
        css(styles.avatarFrame, frameShapeStyle[props.shape], frameSizeStyle[props.size]),
        props.className
      )}
      style={{border: 'solid 2px', borderColor: frameColor}}
    >
      {frameIcon && (
        <span
          className={classNames(css(styles.frameIconSpan, iconSizeStyle[props.size]))}
          style={{backgroundColor: frameColor}}
        >
          <BenchlingIcon
            icon={frameIcon}
            className={css(styles.frameIcon)}
            style={{backgroundColor: frameColor}}
          />
        </span>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  avatarFrame: {
    display: 'inline-block',
    position: 'absolute',
  },
  squareFrame: {
    borderRadius: '2px',
  },
  circleFrame: {
    borderRadius: '50%',
  },
  xxsFrame: {
    height: '22px',
    width: '22px',
  },
  xsFrame: {
    height: '26px',
    width: '26px',
  },
  smallFrame: {
    height: '34px',
    width: '34px',
  },
  largeFrame: {
    height: '70px',
    width: '70px',
  },
  xlFrame: {
    width: '90px',
    height: '90px',
  },
  frameIconSpan: {
    display: 'inline-flex',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    right: '0',
    bottom: '0',
    color: WHITE,
    padding: '3px',
  },
  frameIcon: {
    width: '10px',
    height: '10px',
  },
  iconSmall: {
    width: '12px',
    height: '12px',
    right: '-5px',
    bottom: '-2px',
  },
  iconMedium: {
    width: '16px',
    height: '16px',
    right: '-5px',
    bottom: '-2px',
  },
});
